import React, { useEffect, useRef, useState } from 'react';
import uuidv4 from 'uuid/v4';
import { eventTracking } from '../../tools/eventTracking';
import { dynamicClassName } from '../../tools/tools';
import FloatingSearchBarButton from './FloatingSearchBarButton';

interface FloatingSearchBarInputProps {
  placeholder?: string;
  input: string;
  isFocus: boolean;
  onInputChange: (value: string) => void;
  isDefault?: boolean;
  onFocus?: (value: boolean) => void;
}

const getFormName = (path: string) => {
  return path === '/' ? 'engine-response-home' : 'engine-response-other-page';
};

const FloatingSearchBarInput: React.FC<FloatingSearchBarInputProps> = ({
  isFocus,
  placeholder,
  input,
  onInputChange,
  isDefault,
  onFocus,
}) => {
  const searchBarRef = useRef(null);
  const [isFloating, setIsFloating] = useState<boolean>(false);
  const [path, setPath] = useState<string>('');

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, query: string) => {
    if (e.key === 'Enter') {
      inputValidation(e, query);
      e.preventDefault();
    }
  };

  const inputValidation = (e: any, query: string): void => {
    const chat_id = uuidv4();
    const encodeQuery = encodeURIComponent(query);
    if (typeof window !== 'undefined') {
      e.preventDefault();
      eventTracking('interaction', 'clic-barre-recherche', `clic-barre-recherche - ${query}`);
      window.location.href = `/engine/charentes-engine/chat?chat_id=${chat_id}&query=${encodeQuery}`;
    }
  };

  const handleClickOutside = (event) => {
    if (onFocus === undefined) return;
    if (searchBarRef.current && !searchBarRef.current.contains(event.target)) {
      onFocus(false);
    } else {
      onFocus(true);
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [handleClickOutside]);

  useEffect(() => {
    setPath(window?.location?.pathname);

    const handleScroll = () => {
      const scrollThreshold = 200; // Ajustez cette valeur selon vos besoins

      if (window.scrollY > scrollThreshold) {
        setIsFloating(true);
      } else {
        setIsFloating(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      className={`floatingSearchBar-container ${dynamicClassName('floatingSearchBar-container', isFocus)} ${isFloating ? 'is-floating' : ''}`}
      ref={searchBarRef}
    >
      <form
        className={dynamicClassName('floatingSearchBar-content', isFocus)}
        name={getFormName(path)}
        onSubmit={(e) => e.preventDefault()}
        id={getFormName(path)}
      >
        {/* RGAA 11.1 — label accessible sur le champ de recherche IA */}
        <label htmlFor="floating-search-input" className="visually-hidden">
          Rechercher une activité dans les Charentes
        </label>
        <input
          id="floating-search-input"
          type="text"
          aria-label="Rechercher une activité dans les Charentes"
          placeholder={placeholder || ''}
          onKeyDown={(e) => onKeyDown(e, input)}
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onSubmit={(e) => inputValidation(e, input)}
        />

        <button
          className="floatingSearchBar-button"
          id="floating-search-bar-button"
          type="submit"
          form={getFormName(path)}
          aria-label="Lancer la recherche"
        >
          <FloatingSearchBarButton
            isFocus={isFocus}
            query={input}
            onSubmit={inputValidation}
            isDefault={isDefault}
          />
        </button>
      </form>
    </div>
  );
};

export default FloatingSearchBarInput;
